import { useCallback, useEffect, useState } from 'react';
import { Spin, Alert, message } from 'antd';
import { useParams } from 'react-router';
import { QuestionForm } from './QuestionForm';
import { getQuestion, updateQuestion, QuestionPayload } from '../../api/discovery';
import { DiscoveryQuestion } from '../../api/types';

/**
 * Edit an inventory question as a BARE form — no Refine `<Edit>` page chrome
 * — so it can render in place inside the shared `DetailPage` (see
 * `QuestionDetail.tsx`). Mirrors the console `QuestionEdit`'s bare-form
 * conversion; unlike the console (which fetches/mutates via
 * `@refinedev/core`'s `useOne`/`useUpdate`), this stays hand-rolled
 * (`getQuestion`/`updateQuestion` against `../../api/discovery`) — the same
 * fetch mechanism this file already used before the refactor. The portal is
 * single-tenant/token-scoped, so there is no companyId to thread and no
 * "select a tenant" branch.
 *
 * `onSaved` reports success so `QuestionDetail` flips back to read mode (and
 * refetches its own copy — this component's fetch is independent, hand-rolled
 * state, not a shared query cache, so nothing else does that automatically).
 * `onDirtyChange` is forwarded straight from `QuestionForm` (see that file's
 * "touched" tracking — there's no antd `onValuesChange` to hook here).
 * `hideCancel` suppresses `QuestionForm`'s own Cancel button: the shared
 * `DetailPage` already renders one Cancel in its header, routed through its
 * discard guard.
 *
 * `adminFetch` throws an `HttpError` with `statusCode` on a non-2xx response
 * (see `api/dataProvider.ts`) — derive `notFound` from it so a 404 renders
 * nothing here rather than a misleading "Couldn't load question" Alert (the
 * embedding `QuestionDetail`'s OWN fetch already surfaces the real 404 Result
 * via `DetailPage`; by the time a user could reach this form, that top-level
 * fetch has already succeeded, so this is a defensive fallback for a
 * since-deleted record, not the primary 404 path).
 *
 * All props are optional so a caller can still render `<QuestionEdit/>` bare.
 * `id` is passed explicitly for robustness (at /questions/:id the inferred
 * route param is read directly, not via a Refine `show`/`edit` action); it
 * falls back to the route param.
 */
export interface QuestionEditProps {
  id?: string;
  onSaved?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export function QuestionEdit({ id, onSaved, onDirtyChange }: QuestionEditProps = {}) {
  const params = useParams<{ id: string }>();
  const recordId = id ?? params.id;

  const [question, setQuestion] = useState<DiscoveryQuestion | null>(null);
  const [loadError, setLoadError] = useState<{ statusCode?: number; message?: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    if (!recordId) return;
    setLoadError(null);
    getQuestion(recordId)
      .then(setQuestion)
      .catch((err) => setLoadError(err as { statusCode?: number; message?: string }));
  }, [recordId]);

  useEffect(() => {
    load();
  }, [load]);

  async function onSubmit(payload: QuestionPayload) {
    if (!recordId) return;
    setSubmitting(true);
    try {
      await updateQuestion(recordId, payload);
      message.success('Question saved');
      onSaved?.();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  const notFound = loadError?.statusCode === 404;

  // The embedding QuestionDetail's own fetch already renders the 404 Result
  // via DetailPage — avoid a second, conflicting error surface here.
  if (notFound) return null;
  if (loadError) {
    return (
      <Alert type="error" message="Couldn't load question" description={loadError.message ?? 'Failed to load'} />
    );
  }
  if (!question) return <Spin />;

  return (
    <QuestionForm
      initial={question}
      showDefault
      submitting={submitting}
      onSubmit={onSubmit}
      onCancel={() => undefined}
      hideCancel
      onDirtyChange={onDirtyChange}
    />
  );
}
